<template>
  <el-dialog
    :model-value="modelValue"
    :title="currentTemplateId ? '更新模板配置' : '保存导出配置为模板'"
    width="480px"
    destroy-on-close
    @update:model-value="emit('update:modelValue', $event)"
  >
    <el-form label-position="top" :model="form">
      <el-form-item v-if="!currentTemplateId" label="模板别名" required>
        <el-input
          v-model="form.alias"
          placeholder="例如：周报流量模板、商品点击行为模板"
          maxlength="60"
          show-word-limit
        />
      </el-form-item>
      <el-form-item v-else label="模板别名（可选，留空则保留原名）">
        <el-input
          v-model="form.alias"
          placeholder="留空则保留原模板名称"
          maxlength="60"
          show-word-limit
        />
        <p class="save-hint">将覆盖更新当前载入的模板配置，原别名和可见性保持不变（除非修改）。</p>
      </el-form-item>

      <el-form-item v-if="!currentTemplateId" label="可见范围">
        <el-radio-group v-model="form.visibility">
          <el-radio :label="0">
            <span>个人</span>
            <span class="opt-hint">只有我能看到</span>
          </el-radio>
          <el-radio :label="1">
            <span>公开</span>
            <span class="opt-hint">同项目所有人可见</span>
          </el-radio>
        </el-radio-group>
      </el-form-item>

      <div class="config-preview">
        <strong>{{ currentTemplateId ? '将更新以下配置' : '将保存以下配置' }}</strong>
        <ul>
          <li>项目：{{ projectName || '-' }}（code:{{ projectCode || '-' }}）</li>
          <li>日期范围：{{ props.dateRange?.[0] }} ~ {{ props.dateRange?.[1] }}</li>
          <li>Sheet 数量：{{ props.sheets?.length || 0 }} 个</li>
          <li>日期方向：{{ props.orientation === 'date-row' ? '日期做表头' : '日期做列头' }}</li>
        </ul>
      </div>
    </el-form>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">取消</el-button>
      <el-button type="primary" :loading="saving" @click="handleSave">保存模板</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { saveTemplate, updateTemplate, fetchTemplates } from '../reportDataTemplateApi'

const props = defineProps({
  modelValue:     { type: Boolean, default: false },
  projectCode:    { type: String,  default: '' },
  projectName:    { type: String,  default: '' },
  dateRange:      { type: Array,   default: () => [] },
  sheets:         { type: Array,   default: () => [] },
  orientation:    { type: String,  default: 'date-row' },
  configSnapshot: { type: Object,  default: () => ({}) },
  /** 当前已载入的模板 ID，有值时保存为覆盖更新，否则新增 */
  currentTemplateId: { type: [Number, String], default: null }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const saving = ref(false)
const form = reactive({ alias: '', visibility: 0 })

// 载入模板时同步别名到表单
watch(() => props.currentTemplateId, (id) => {
  if (!id) { form.alias = ''; form.visibility = 0 }
})

const isUpdate = () => !!props.currentTemplateId

async function handleSave() {
  if (!form.alias.trim() && !isUpdate()) {
    ElMessage.warning('请填写模板别名')
    return
  }
  saving.value = true
  try {
    let savedTemplateId = props.currentTemplateId
    if (isUpdate()) {
      await updateTemplate(props.currentTemplateId, {
        alias:      form.alias.trim() || undefined,
        configJson: props.configSnapshot
      })
      ElMessage.success('模板配置已更新')
    } else {
      if (!form.alias.trim()) { ElMessage.warning('请填写模板别名'); return }
      const aliasTrim = form.alias.trim()
      await saveTemplate({
        alias:       aliasTrim,
        projectCode: props.projectCode,
        projectName: props.projectName,
        visibility:  form.visibility,
        configJson:  props.configSnapshot
      })
      const rows = await fetchTemplates(props.projectCode)
      const created = (Array.isArray(rows) ? rows : []).find((r) => r.alias === aliasTrim)
      savedTemplateId = created?.id || null
      ElMessage.success('模板保存成功')
    }
    emit('saved', { templateId: savedTemplateId })
    // 不关闭抽屉，只关闭弹窗
    emit('update:modelValue', false)
    form.alias = ''
    form.visibility = 0
  } catch (err) {
    ElMessage.error('保存失败：' + (err.message || '未知错误'))
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.opt-hint {
  font-size: 12px;
  color: #94a3b8;
  margin-left: 4px;
}

.config-preview {
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 13px;
}

.config-preview strong {
  color: #0f172a;
  display: block;
  margin-bottom: 8px;
}

.config-preview ul {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  line-height: 2;
}
</style>
